import React, { useState, useRef, useCallback } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import { createRoot } from 'react-dom/client';
import type { WidgetMap, RenderWidgetOptions } from './types';

interface DashboardRecord {
  Title: string;
  Code: string;
  IsVisible: boolean;
  Settings: Array<{ Key: string; Value: string }>;
}

interface DynamicDashboardProps {
  records: DashboardRecord[];
  widgetMap: WidgetMap;
  renderWidget: (
    key: string,
    container: HTMLElement,
    options?: RenderWidgetOptions,
  ) => void;
}

/**
 * Tabbed dashboard used by `renderDynamically`. Each tab corresponds to a
 * visible widget from the server-provided dashboard configuration.
 */
const DynamicDashboard: React.FC<DynamicDashboardProps> = ({
  records,
  renderWidget,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);

  const renderActiveWidget = useCallback(
    (index: number) => {
      const record = records[index];
      if (!record || !panelRef.current) return;

      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }

      panelRef.current.innerHTML = '';

      const container = document.createElement('div');
      panelRef.current.appendChild(container);

      const settingsProps: Record<string, unknown> = {};
      record.Settings.forEach((s) => {
        try {
          settingsProps[s.Key] = JSON.parse(s.Value);
        } catch {
          settingsProps[s.Key] = s.Value;
        }
      });

      renderWidget(record.Code, container, settingsProps);
    },
    [records, renderWidget],
  );

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setActiveIndex(newIndex);
    renderActiveWidget(newIndex);
  };

  React.useEffect(() => {
    renderActiveWidget(0);
  }, [renderActiveWidget]);

  if (records.length === 0) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        p={4}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs
        value={activeIndex}
        onChange={handleTabChange}
        variant='scrollable'
        scrollButtons='auto'
      >
        {records.map((r, i) => (
          <Tab
            key={r.Code}
            label={r.Title}
            value={i}
          />
        ))}
      </Tabs>
      <Box
        ref={panelRef}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default DynamicDashboard;
